import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag, User } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useSiteSettings, ArtistName } from "../context/SiteSettingsContext";

const linkDest = (it) => (it.route === "CUSTOM" ? it.customUrl || "#" : it.route);
const isExternal = (it) => it.newTab || /^https?:\/\//.test(linkDest(it));
const tid = (prefix, it) => `${prefix}-${(it.title || "item").toLowerCase().replace(/\s+/g, "-")}`;

function NavItem({ it, className, testPrefix }) {
  const to = linkDest(it);
  if (isExternal(it)) {
    return (
      <a href={to} target={it.newTab ? "_blank" : undefined} rel="noreferrer"
        data-testid={tid(testPrefix, it)} className={className}>{it.title}</a>
    );
  }
  return <Link to={to} data-testid={tid(testPrefix, it)} className={className}>{it.title}</Link>;
}

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { count, setOpen: setCartOpen } = useCart();
  const { user } = useAuth();
  const { nav, profile } = useSiteSettings();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location]);

  return (
    <>
      <header
        data-testid="main-nav"
        className={`fixed top-0 left-0 right-0 z-[9990] transition-colors duration-500 ${
          scrolled ? "bg-black/60 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <Link
            to="/"
            data-testid="nav-logo"
            className="font-serif text-lg md:text-xl tracking-[0.15em] text-white hover:text-gold transition-colors"
          >
            <ArtistName name={profile.displayName} />
          </Link>

          <nav className="hidden md:flex items-center gap-10">
            {nav.map((it) => (
              <NavItem key={it.id || it.title} it={it} testPrefix="nav"
                className="font-mono text-[11px] tracking-[0.2em] text-white/70 hover:text-gold transition-colors duration-300" />
            ))}
            {user && user.dimensionV === "active" && (
              <Link to="/dimension-v" data-testid="nav-my-dimension-v"
                className="font-mono text-[11px] tracking-[0.2em] text-gold hover:text-white transition-colors duration-300">
                MY DIMENSION V
              </Link>
            )}
            <Link
              to={user ? "/account" : "/login"}
              data-testid="nav-account"
              aria-label="Account"
              className="text-white/70 hover:text-gold transition-colors"
            >
              <User size={18} />
            </Link>
            <button
              onClick={() => setCartOpen(true)}
              data-testid="nav-cart"
              aria-label="Open cart"
              className="relative text-white/70 hover:text-gold transition-colors"
            >
              <ShoppingBag size={18} />
              {count > 0 && (
                <span className="absolute -top-2 -right-2 bg-gold text-black font-mono text-[9px] w-4 h-4 flex items-center justify-center rounded-full">
                  {count}
                </span>
              )}
            </button>
          </nav>

          <div className="md:hidden flex items-center gap-5">
            <button
              onClick={() => setCartOpen(true)}
              data-testid="nav-cart-mobile"
              aria-label="Open cart"
              className="relative text-white"
            >
              <ShoppingBag size={22} />
              {count > 0 && (
                <span className="absolute -top-2 -right-2 bg-gold text-black font-mono text-[9px] w-4 h-4 flex items-center justify-center rounded-full">
                  {count}
                </span>
              )}
            </button>
            <button
              className="text-white"
              onClick={() => setOpen(true)}
              data-testid="nav-menu-open"
              aria-label="Open menu"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[9995] bg-[#050505] md:hidden flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            data-testid="mobile-menu"
          >
            <div className="h-20 px-6 flex items-center justify-between border-b border-white/10">
              <span className="font-serif text-lg tracking-[0.15em]"><ArtistName name={profile.displayName} /></span>
              <button onClick={() => setOpen(false)} data-testid="nav-menu-close" aria-label="Close menu">
                <X size={24} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center gap-2 px-8">
              {nav.map((it, i) => (
                <motion.div
                  key={it.id || it.title}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.07 }}
                >
                  <NavItem it={it} testPrefix="mobile-nav"
                    className="font-serif text-5xl text-white/90 hover:text-gold transition-colors block py-2" />
                </motion.div>
              ))}
              {user && user.dimensionV === "active" && (
                <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + nav.length * 0.07 }}>
                  <Link to="/dimension-v" className="font-serif text-5xl text-gold hover:text-white transition-colors block py-2" data-testid="mobile-nav-dimension-v">
                    DIMENSION V
                  </Link>
                </motion.div>
              )}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + nav.length * 0.07 }}
              >
                <Link
                  to={user ? "/account" : "/login"}
                  className="font-serif text-5xl text-white/90 hover:text-gold transition-colors block py-2"
                  data-testid="mobile-nav-account"
                >
                  {user ? "ACCOUNT" : "SIGN IN"}
                </Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
